import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPopup,
  updateProfile
} from 'firebase/auth';
import { auth, provider, db } from '../lib/firebase';
import { doc, getDoc, setDoc, query, collection, where, getDocs } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [userData, setUserData] = useState(null);

  // Check if user is approved
  const checkUserApproval = async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.isApproved === true && (userData.role === 'admin' || userData.role === 'superadmin');
      }
      return false;
    } catch (error) {
      console.error('Error checking approval:', error);
      return false;
    }
  };

  // Sign up with email/password (pending admin approval)
  const signUp = async (email, password, displayName, phone = '', address = '') => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(userCredential.user, { displayName });
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      
      // Create user document in Firestore with pending approval
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: email,
        displayName: displayName,
        phone: phone,
        address: address,
        role: 'pending', // pending, admin, superadmin, user, rejected
        isApproved: false,
        createdAt: new Date().toISOString(),
        emailVerified: false,
        applicationStatus: 'pending_review'
      });
      
      // Create notification for super admin
      const superAdminsQuery = query(collection(db, 'users'), where('role', '==', 'superadmin'));
      const superAdminsSnapshot = await getDocs(superAdminsQuery);
      
      for (const superAdminDoc of superAdminsSnapshot.docs) {
        await setDoc(doc(db, 'notifications', `${superAdminDoc.id}_${userCredential.user.uid}_${Date.now()}`), {
          userId: superAdminDoc.id,
          applicantId: userCredential.user.uid,
          userEmail: email,
          userName: displayName,
          type: 'new_admin_application',
          message: `New admin application from ${displayName} (${email})`,
          read: false,
          createdAt: new Date().toISOString()
        });
      }
      
      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  // Sign in with email/password (only if admin/superadmin and approved)
  const signIn = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Check if email is verified
      if (!user.emailVerified) {
        throw new Error('Please verify your email before signing in');
      }
      
      // Check user role and approval status
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        throw new Error('User account not found. Please contact support.');
      }
      
      const userData = userDoc.data();
      
      // Check if user is approved admin or superadmin
      if ((userData.role !== 'admin' && userData.role !== 'superadmin') || !userData.isApproved) {
        await signOut(auth);
        throw new Error('Access denied. Only approved administrators can sign in.');
      }
      
      return user;
    } catch (error) {
      throw error;
    }
  };

  // Sign in with Google (only if admin/superadmin and approved)
// Sign in with Google (now handles new users differently)
const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;
    
    // Check if user exists in Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    if (!userDoc.exists()) {
      // New user - create basic profile and return special flag
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        displayName: user.displayName || user.email.split('@')[0],
        phone: '',
        address: '',
        role: 'pending_application', // Special role for incomplete applications
        isApproved: false,
        createdAt: new Date().toISOString(),
        emailVerified: user.emailVerified || false,
        applicationStatus: 'incomplete', // Needs to complete application
        authProvider: 'google',
      });
      
      // Return a special flag to indicate this is a new user who needs to complete application
      return { user, needsCompletion: true };
    }
    
    const userData = userDoc.data();
    
    // Check if user has incomplete application
    if (userData.applicationStatus === 'incomplete' || userData.role === 'pending_application') {
      return { user, needsCompletion: true };
    }
    
    // Existing user - check if approved admin or superadmin
    if ((userData.role !== 'admin' && userData.role !== 'superadmin') || !userData.isApproved) {
      await signOut(auth);
      throw new Error('Access denied. Only approved administrators can sign in.');
    }
    
    return { user, needsCompletion: false };
  } catch (error) {
    throw error;
  }
};

  // Resend verification email
  const resendVerificationEmail = async () => {
    try {
      await sendEmailVerification(auth.currentUser);
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      return true;
    } catch (error) {
      throw error;
    }
  };

  // Check user role and approval status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      
      if (user) {
        // Check if email is verified
        if (!user.emailVerified) {
          setUserRole(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsApproved(false);
          setUserData(null);
          setLoading(false);
          return;
        }
        
        // Get user role and approval from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const role = userData.role;
          const isApproved = userData.isApproved || false;
          
          setUserData(userData);
          setUserRole(role);
          setIsSuperAdmin(role === 'superadmin' && isApproved);
          setIsAdmin((role === 'admin' || role === 'superadmin') && isApproved);
          setIsApproved(isApproved);
          
          // If user is not approved admin/superadmin, sign them out
          if ((role !== 'admin' && role !== 'superadmin') || !isApproved) {
            await signOut(auth);
            setCurrentUser(null);
            setUserRole(null);
            setIsAdmin(false);
            setIsSuperAdmin(false);
            setIsApproved(false);
            setUserData(null);
          }
        } else {
          setUserRole(null);
          setIsAdmin(false);
          setIsSuperAdmin(false);
          setIsApproved(false);
          setUserData(null);
        }
      } else {
        setUserRole(null);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsApproved(false);
        setUserData(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper functions for permission checks
  const canEditUser = (targetUser) => {
    if (!currentUser) return false;
    if (isSuperAdmin) return true;
    if (targetUser.role === 'superadmin') return false;
    if (targetUser.id === currentUser.uid) return true;
    return false;
  };

  const canDeleteUser = (targetUser) => {
    if (!currentUser) return false;
    if (!isSuperAdmin) return false;
    if (targetUser.role === 'superadmin') return false;
    if (targetUser.id === currentUser.uid) return false;
    return true;
  };

  const canApproveUser = (targetUser) => {
    if (!currentUser) return false;
    if (isSuperAdmin) return true;
    if (targetUser.role === 'superadmin') return false;
    return isAdmin && targetUser.role === 'pending';
  };

  const canChangeRole = (targetUser, newRole) => {
    if (!currentUser) return false;
    if (!isSuperAdmin) return false;
    if (targetUser.role === 'superadmin' && newRole !== 'superadmin') return false;
    return true;
  };

  const value = {
    currentUser,
    userRole,
    userData,
    isAdmin,
    isSuperAdmin,
    isApproved,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    forgotPassword,
    resendVerificationEmail,
    checkUserApproval,
    // Permission helper functions
    canEditUser,
    canDeleteUser,
    canApproveUser,
    canChangeRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};